// app/components/BuySell.tsx
'use client'

import React, { useState, useCallback, memo, useEffect, useRef, useMemo } from 'react'
import styles from './BuySell.module.css'
import { usePathname } from 'next/navigation'
import { SymbolSearchModal } from '../../SymbolSearch/SymbolSearch'
import { ConnectorManager } from '../ConnectorManager'
import { createVisualInteractionUtils, AnimationStatus } from '../../AITerminal/utils/visualInteractions'
import { useAITerminal } from '../../AITerminal/AITerminal'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getSecureItem } from '../utils/secureStorage'
import { useOrderStatus } from './hooks/useOrderStatus'
import { usePriceCalculations } from './hooks/usePriceCalculations'
import { usePercentageSelection } from './hooks/usePercentageSelection'
import { validateQuantity, parseCurrencyPair } from './utils/validationUtils'
import PercentageBars from './components/PercentageBars'

/**
 * @uiComponent
 * @name BuySell
 * @description The BuySell component allows users to place buy and sell orders for cryptocurrencies on the Binance platform.
 * It supports market orders, limit orders, and stop orders. Features real-time symbol selection,
 * form validation, and integration with the Binance connector for order execution.
 * @location Right side panel of the trading interface
 * @prop symbol The cryptocurrency trading pair symbol (e.g., BTCUSDT, ETHUSDT)
 * @prop initialPrice Optional initial price for limit orders
 * @prop initialAction Optional initial action (BUY/SELL) to preset the interface
 */

/**
 * @uiWorkflow
 * @name Place a Market Order on Binance
 * @description How to quickly execute a market order for immediate cryptocurrency purchase or sale
 * @platform binance
 * @step 1 Navigate to the trading terminal and select your desired crypto pair [component: SymbolPage] [action: navigate]
 * @step 2 In the Buy/Sell panel on the right side, ensure "Market" tab is selected [component: BuySell] [action: click]
 * @step 3 Enter the quantity of cryptocurrency you want to trade [component: BuySell] [action: input]
 * @step 4 Choose whether you want to buy or sell the cryptocurrency [component: BuySell] [action: select]
 * @step 5 Review the current market conditions [component: BuySell] [action: observe]
 * @step 6 Click the "Buy" or "Sell" button to place your market order [component: BuySell] [action: click]
 * @step 7 Monitor the order status for confirmation [component: BuySell] [action: observe]
 */

/**
 * @uiWorkflow
 * @name Place a Limit Order on Binance
 * @description How to place a limit order with a specific target price for cryptocurrency trading
 * @platform binance
 * @step 1 Navigate to your desired cryptocurrency pair in the trading terminal [component: SymbolPage] [action: navigate]
 * @step 2 In the Buy/Sell panel, click on the "Limit" tab [component: BuySell] [action: click]
 * @step 3 Enter your desired limit price for the trade [component: BuySell] [action: input]
 * @step 4 Enter the quantity of cryptocurrency you want to trade [component: BuySell] [action: input]
 * @step 5 Select Buy or Sell based on your trading strategy [component: BuySell] [action: select]
 * @step 6 Review the price compared to current market price [component: BuySell] [action: observe]
 * @step 7 Click the appropriate button to place your limit order [component: BuySell] [action: click]
 * @step 8 Wait for order confirmation and monitor in open orders [component: BuySell] [action: observe]
 */

/**
 * @uiWorkflow
 * @name Place a Stop Order on Binance
 * @description How to place a stop order to trigger a trade at a specific price level
 * @platform binance
 * @step 1 Navigate to the trading terminal and select your crypto pair [component: SymbolPage] [action: navigate]
 * @step 2 In the Buy/Sell panel, select the "Stop" tab [component: BuySell] [action: click]
 * @step 3 Enter the trigger price at which the order should activate [component: BuySell] [action: input]
 * @step 4 Optionally enter a limit price for stop-limit orders [component: BuySell] [action: input]
 * @step 5 Enter the quantity of cryptocurrency to trade [component: BuySell] [action: input]
 * @step 6 Choose Buy or Sell depending on your trading objective [component: BuySell] [action: select]
 * @step 7 Review the trigger conditions and order details [component: BuySell] [action: observe]
 * @step 8 Click the "Buy" or "Sell" button to place the stop order [component: BuySell] [action: click]
 * @step 9 Monitor the order status and trigger conditions [component: BuySell] [action: observe]
 */

/**
 * @uiWorkflow
 * @name Use Symbol Search in Binance BuySell Panel
 * @description How to search and select different cryptocurrency pairs for trading
 * @platform binance
 * @step 1 Locate the symbol selector in the Buy/Sell panel header [component: BuySell] [action: observe]
 * @step 2 Click on the current symbol dropdown button [component: BuySell] [action: click]
 * @step 3 The symbol search modal will open with popular pairs [component: SymbolSearchModal] [action: observe]
 * @step 4 Type the cryptocurrency name you want to trade [component: SymbolSearchModal] [action: input] [notes: e.g., BTC, ETH, BNB]
 * @step 5 Select from the filtered results or popular symbols [component: SymbolSearchModal] [action: click]
 * @step 6 The symbol will update in the Buy/Sell panel [component: BuySell] [action: observe]
 * @step 7 Proceed with placing your order for the selected pair [component: BuySell] [action: continue]
 */

interface BuySellProps {
  symbol?: string
  initialPrice?: string
  initialAction?: 'BUY' | 'SELL' | null
}

const TABS = ['Market', 'Limit', 'Stop'];

function BuySellComponent({ symbol: propSymbol, initialPrice = '', initialAction = null }: BuySellProps) {
  const pathname = usePathname()
  const queryClient = useQueryClient()
  
  // Extract the first segment after the domain from the URL path
  const pathSegments = pathname ? pathname.split('/').filter(Boolean) : []
  const symbolFromPath = pathSegments[0] || 'btcusdt'

  // If the symbol ends with .NS or .BS, use a fallback symbol
  const defaultSymbol =
    symbolFromPath.toUpperCase().endsWith('.NS') ||
    symbolFromPath.toUpperCase().endsWith('.BS')
      ? 'btcusdt'
      : symbolFromPath

  // State for symbol selection and custom input
  const [currentSymbol, setCurrentSymbol] = useState(propSymbol || defaultSymbol)
  const [isSymbolPopupOpen, setIsSymbolPopupOpen] = useState(false)
  const [symbolInput, setSymbolInput] = useState('')
  const [popularSymbols] = useState([
    'BTCUSDT',
    'ETHUSDT',
    'BNBUSDT',
    'SOLUSDT',
    'LTCUSDT'
  ])
  
  const [activeTab, setActiveTab] = useState<string>(initialPrice ? 'Limit' : 'Market')
  const [price, setPrice] = useState(initialPrice)
  const [triggerPrice, setTriggerPrice] = useState('')

  // TP/SL State Management
  const [tpSlEnabled, setTpSlEnabled] = useState(false)
  const [tpPrice, setTpPrice] = useState('')
  const [slTriggerPrice, setSlTriggerPrice] = useState('')
  const [slType, setSlType] = useState<'MARKET' | 'LIMIT'>('MARKET')
  const [slLimitPrice, setSlLimitPrice] = useState('')

  // Extract base and quote currencies from symbol (e.g., BTCUSDT -> BTC, USDT)
  const { baseCurrency, quoteCurrency } = useMemo(() => {
    return parseCurrencyPair(currentSymbol)
  }, [currentSymbol])

  // Fetch user assets using React Query with optimized performance
  const { data: userAssets = [], isLoading: assetsLoading, refetch: refetchAssets, error: assetsError } = useQuery({
    queryKey: ['userAssets', getSecureItem('broker_enabled')],
    queryFn: async () => {
      const connectorManager = ConnectorManager.getInstance()
      
      if (!connectorManager) {
        throw new Error('Connector manager not found')
      }
      
      // Get the enabled connector (binance or ninx)
      const enabledConnector = getSecureItem('broker_enabled')
      const connector = enabledConnector === 'binance' ? 
        connectorManager.getConnector('binance') :
        enabledConnector === 'ninx' ?
          connectorManager.getConnector('ninx') :
          null
      
      if (!connector) {
        throw new Error(`${enabledConnector || 'No'} connector not found or not connected`)
      }
      
      return await connector.getUserAssets()
    },
    staleTime: 30 * 1000, // 30 seconds - balance data doesn't change frequently
    gcTime: 5 * 60 * 1000, // 5 minute cache - keep longer for better UX
    retry: 2, // More retries for critical balance data
    refetchInterval: 60 * 1000, // Reduced to every 60 seconds - balances don't change often
    enabled: !!getSecureItem('broker_enabled'), // Only fetch when broker is enabled
    refetchOnWindowFocus: false, // Disable to reduce unnecessary requests
    refetchOnReconnect: true, // Refetch when connection is restored
  })

  // Get available balances for base and quote currencies with better handling
  const baseBalance = useMemo(() => {
    if (!userAssets || userAssets.length === 0) return 0
    const asset = userAssets.find(a => a.asset === baseCurrency)
    return asset?.free || 0
  }, [userAssets, baseCurrency])

  const quoteBalance = useMemo(() => {
    if (!userAssets || userAssets.length === 0) return 0
    const asset = userAssets.find(a => a.asset === quoteCurrency)
    return asset?.free || 0
  }, [userAssets, quoteCurrency])

  // Determine if we have valid balance data
  const hasBalanceData = useMemo(() => {
    return !assetsLoading && !assetsError && userAssets && userAssets.length > 0
  }, [assetsLoading, assetsError, userAssets])

  // Fetch current price using React Query with optimized performance
  const { data: tickerData, isLoading: priceLoading } = useQuery({
    queryKey: ['binancePrice', currentSymbol, getSecureItem('broker_enabled')],
    queryFn: async () => {
      const connectorManager = ConnectorManager.getInstance()
      
      if (!connectorManager) {
        throw new Error('Connector manager not found')
      }
      
      // Get the enabled connector (binance or ninx)
      const enabledConnector = getSecureItem('broker_enabled')
      const connector = enabledConnector === 'binance' ? 
        connectorManager.getConnector('binance') :
        enabledConnector === 'ninx' ?
          connectorManager.getConnector('ninx') :
          null
      
      if (!connector || typeof connector.getTickers !== 'function') {
        throw new Error(`${enabledConnector || 'No'} connector not found or doesn't support price fetching`)
      }
      
      // Fetch ticker data for the current symbol
      const tickers = await connector.getTickers({ symbol: currentSymbol })
      
      if (tickers && tickers.length > 0) {
        // Find exact match or use first ticker
        const matchedTicker = tickers.find(ticker => 
          ticker.symbol.toUpperCase() === currentSymbol.toUpperCase()
        ) || tickers[0]
        
        return matchedTicker
      }
      
      return null
    },
    staleTime: 2 * 1000, // 2 seconds - price data needs to be fresh
    gcTime: 2 * 60 * 1000, // 2 minute cache
    retry: 1, // Quick retry for price data
    refetchInterval: 5 * 1000, // Every 5 seconds for real-time pricing
    enabled: !!getSecureItem('broker_enabled') && !!currentSymbol, // Only fetch when broker is enabled and symbol exists
    refetchOnWindowFocus: true, // Keep this for price data as it's critical
    refetchIntervalInBackground: false, // Don't fetch when tab is not active
  })

  // Derive price state from ticker data instead of using useEffect
  const lastPrice = tickerData?.price || null
  const priceChangePercent = tickerData?.priceChangePercent || null

  // Price calculations and form state
  const {
    amount,
    total,
    setAmount,
    setTotal,
    handleAmountChange: handleAmountChangeBase,
    handleTotalChange: handleTotalChangeBase,
    isEditingTotal,
    setIsEditingTotal,
    calculateTotal,
    calculateAmount,
    resetForm
  } = usePriceCalculations({ activeTab, price, lastPrice })

  // Percentage selection management
  const {
    selectedAmountPercentage,
    selectedTotalPercentage,
    handleAmountPercentageClick,
    handleTotalPercentageClick,
    resetAmountPercentage,
    resetTotalPercentage
  } = usePercentageSelection({
    baseBalance,
    quoteBalance,
    lastPrice,
    onAmountChange: handleAmountChangeBase,
    onTotalChange: handleTotalChangeBase,
    calculateTotal,
    calculateAmount
  })

  // Quantity validation using utility
  const quantityValidation = amount ? validateQuantity(parseFloat(amount), currentSymbol, baseCurrency) : { isValid: true }



  // Enhanced handlers that include percentage reset
  const handleAmountChange = useCallback((value: string) => {
    handleAmountChangeBase(value)
    // Reset both percentage states when manually typing (same as old version)
    resetAmountPercentage()
    resetTotalPercentage()
  }, [handleAmountChangeBase, resetAmountPercentage, resetTotalPercentage])

  const handleTotalChange = useCallback((value: string) => {
    handleTotalChangeBase(value)
    // Reset both percentage states when manually typing (same as old version)
    resetAmountPercentage()
    resetTotalPercentage()
  }, [handleTotalChangeBase, resetAmountPercentage, resetTotalPercentage])



  const handleSymbolChange = useCallback((newSymbol: string) => {
    const formattedSymbol = newSymbol.toUpperCase()
    setCurrentSymbol(formattedSymbol)
    setIsSymbolPopupOpen(false)
    setSymbolInput('')
    // Clear amount and total when symbol changes
    resetForm()
    // Also reset percentage selections when symbol changes
    resetAmountPercentage()
    resetTotalPercentage()
  }, [setCurrentSymbol, setIsSymbolPopupOpen, setSymbolInput, resetForm, resetAmountPercentage, resetTotalPercentage])

  const [isLoading, setIsLoading] = useState(false)
  
  // Order status management
  const {
    orderStatus,
    setOrderStatusWithTimer,
    setOrderStatusError,
    closeOrderStatus
  } = useOrderStatus()

  // AI Terminal integration
  const { registerComponent } = useAITerminal()
  
  // Visual animation state
  const [isAnimating, setIsAnimating] = useState(false)
  const [lastAction, setLastAction] = useState<string>('')
  
  // Refs for visual interactions
  const containerRef = useRef<HTMLDivElement>(null)
  const priceRef = useRef<HTMLInputElement>(null)
  const triggerPriceRef = useRef<HTMLInputElement>(null)
  const amountRef = useRef<HTMLInputElement>(null)
  const totalRef = useRef<HTMLInputElement>(null)
  const buyButtonRef = useRef<HTMLButtonElement>(null)
  const sellButtonRef = useRef<HTMLButtonElement>(null)
  const marketTabRef = useRef<HTMLDivElement>(null)
  const limitTabRef = useRef<HTMLDivElement>(null)
  const stopTabRef = useRef<HTMLDivElement>(null)
  const symbolButtonRef = useRef<HTMLButtonElement>(null)
  
  // Create visual interaction utilities
  const visualUtils = createVisualInteractionUtils()

  // Visual order execution function
  const executeVisualOrder = useCallback(async (params: any) => {
    setIsAnimating(true)
    // Determine quote currency from the symbol (e.g., BTCUSDT -> USDT, ETHBUSD -> BUSD)
    const getQuoteCurrency = (symbol: string) => {
      if (symbol.endsWith('USDT')) return 'USDT'
      if (symbol.endsWith('BUSD')) return 'BUSD' 
      if (symbol.endsWith('BTC')) return 'BTC'
      if (symbol.endsWith('ETH')) return 'ETH'
      if (symbol.endsWith('BNB')) return 'BNB'
      return 'quote currency' // fallback
    }
    
    const orderDescription = params.quantity 
      ? `${params.quantity} ${params.symbol}` 
      : `${params.total} ${getQuoteCurrency(params.symbol)} worth of ${params.symbol}`
    setLastAction(`🎬 AI is placing ${params.side} order for ${orderDescription}`)
    
    // Scroll the component into view first
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      })
      await new Promise(resolve => setTimeout(resolve, 800)) // Wait for scroll to complete
    }
    
    try {
      // Step 1: Change symbol if different from current
      if (params.symbol && params.symbol.toUpperCase() !== currentSymbol.toUpperCase()) {
        setLastAction(`🔄 Changing symbol to ${params.symbol}...`)
        await visualUtils.selectSymbol(symbolButtonRef as React.RefObject<HTMLButtonElement>, params.symbol.toUpperCase(), handleSymbolChange)
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      // Step 2: Switch to correct tab
      const tabMap = { 'MARKET': 'Market', 'LIMIT': 'Limit', 'STOP': 'Stop' }
      const tabRefMap = { 'Market': marketTabRef, 'Limit': limitTabRef, 'Stop': stopTabRef }
      const targetTabType = params.type || 'MARKET'
      const targetTabName = tabMap[targetTabType as keyof typeof tabMap]
      
      console.log(`🔍 Tab Debug: activeTab="${activeTab}", targetTabType="${targetTabType}", targetTabName="${targetTabName}"`)
      
      if (targetTabName && activeTab !== targetTabName) {
        setLastAction(`🖱️ Switching to ${targetTabName} tab...`)
        const targetTabRef = tabRefMap[targetTabName as keyof typeof tabRefMap]
        
        if (targetTabRef?.current) {
          console.log(`🖱️ Clicking ${targetTabName} tab element:`, targetTabRef.current)
          
          // Visual feedback first
          await visualUtils.clickElement(targetTabRef as React.RefObject<HTMLElement>)
          
          // Manually trigger the tab switch with proper state updates
          setActiveTab(targetTabName)
          
          // Clear form fields based on tab type (same logic as onClick handler)
          if (targetTabName === 'Market') {
            setPrice('')
            setTriggerPrice('')
          } else if (targetTabName === 'Limit') {
            setTriggerPrice('')
          } else if (targetTabName === 'Stop') {
            setPrice('')
          }
          
          await new Promise(resolve => setTimeout(resolve, 500))
          console.log(`📋 After tab switch, activeTab is now: "${targetTabName}"`)
        } else {
          console.log(`❌ Tab ref not found for ${targetTabName}`)
        }
      } else {
        console.log(`✅ Already on correct tab: ${activeTab}`)
      }
      
      // Step 3: Fill amount or total
      if (params.quantity) {
        setLastAction(`⌨️ Typing quantity: ${params.quantity}`)
        await visualUtils.typeIntoInput(amountRef as React.RefObject<HTMLInputElement>, params.quantity.toString(), setAmount)
        await new Promise(resolve => setTimeout(resolve, 500))
      } else if (params.total) {
        setLastAction(`⌨️ Typing total: ${params.total}`)
        await visualUtils.typeIntoInput(totalRef as React.RefObject<HTMLInputElement>, params.total.toString(), setTotal)
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      // Step 4: Fill trigger price if stop order (do this first for stop orders)
      if (params.type === 'STOP' && params.triggerPrice && triggerPriceRef.current) {
        setLastAction(`⌨️ Typing trigger price: ${params.triggerPrice}`)
        await visualUtils.typeIntoInput(triggerPriceRef as React.RefObject<HTMLInputElement>, params.triggerPrice.toString(), setTriggerPrice)
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      // Step 5: Fill price if limit order OR stop-limit order
      if (params.price && priceRef.current && (params.type === 'LIMIT' || params.type === 'STOP')) {
        const priceType = params.type === 'STOP' ? 'price' : 'price'
        setLastAction(`⌨️ Typing ${priceType}: ${params.price}`)
        await visualUtils.typeIntoInput(priceRef as React.RefObject<HTMLInputElement>, params.price.toString(), setPrice)
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      // Step 6: Submit order
      setLastAction('🚀 Submitting order...')
      const buttonRef = params.side === 'BUY' ? buyButtonRef : sellButtonRef
      if (buttonRef.current) {
        await visualUtils.clickButton(buttonRef as React.RefObject<HTMLButtonElement>)
      }
      
      setLastAction(`✅ ${params.side} order completed successfully!`)
      
      const finalOrderDescription = params.quantity 
        ? `${params.quantity} ${params.symbol}` 
        : `${params.total} ${getQuoteCurrency(params.symbol)} worth of ${params.symbol}`
      
      return {
        success: true,
        orderId: `visual_real_${Date.now()}`,
        message: `${params.side} order for ${finalOrderDescription} placed successfully`
      }
      
    } catch (error) {
      setLastAction(`❌ Error: ${error}`)
      return {
        success: false,
        error: `Failed to execute visual order: ${error}`
      }
    } finally {
      setTimeout(() => {
        setIsAnimating(false)
        setLastAction('')
      }, 2000)
    }
  }, [activeTab, visualUtils, currentSymbol, handleSymbolChange])

  const placeOrder = useCallback(async (side: 'BUY' | 'SELL') => {
    if (!amount) {
      setOrderStatusError('Please enter an amount')
      return
    }
    
    if (activeTab === 'Limit' && !price) {
      setOrderStatusError('Please enter a limit price')
      return
    }
    
    if (activeTab === 'Stop' && !triggerPrice) {
      setOrderStatusError('Please enter a trigger price')
      return
    }

    // TP/SL validation for Limit orders
    if (activeTab === 'Limit' && tpSlEnabled) {
      const hasTp = tpPrice && parseFloat(tpPrice) > 0
      const hasSl = slTriggerPrice && parseFloat(slTriggerPrice) > 0
      
      if (!hasTp && !hasSl) {
        setOrderStatusError('Please enter at least one TP or SL price when TP/SL is enabled')
        return
      }
      
      if (hasTp) {
        const tpPriceNum = parseFloat(tpPrice)
        const limitPriceNum = parseFloat(price)
        
        // Validate TP price logic
        if (side === 'BUY' && tpPriceNum <= limitPriceNum) {
          setOrderStatusError('Take Profit price must be higher than limit price for BUY orders')
          return
        }
        if (side === 'SELL' && tpPriceNum >= limitPriceNum) {
          setOrderStatusError('Take Profit price must be lower than limit price for SELL orders')
          return
        }
      }
      
      if (hasSl) {
        const slTriggerNum = parseFloat(slTriggerPrice)
        const limitPriceNum = parseFloat(price)
        
        // Validate SL trigger price logic
        if (side === 'BUY' && slTriggerNum >= limitPriceNum) {
          setOrderStatusError('Stop Loss trigger must be lower than limit price for BUY orders')
          return
        }
        if (side === 'SELL' && slTriggerNum <= limitPriceNum) {
          setOrderStatusError('Stop Loss trigger must be higher than limit price for SELL orders')
          return
        }
        
        // Validate SL limit price if provided
        if (slType === 'LIMIT' && slLimitPrice) {
          const slLimitNum = parseFloat(slLimitPrice)
          if (side === 'BUY' && slLimitNum <= slTriggerNum) {
            setOrderStatusError('Stop Loss limit price must be higher than trigger price for BUY orders')
            return
          }
          if (side === 'SELL' && slLimitNum >= slTriggerNum) {
            setOrderStatusError('Stop Loss limit price must be lower than trigger price for SELL orders')
            return
          }
        }
      }
    }

    setIsLoading(true)
    setOrderStatusError('Placing order...')

    try {
      // Validate and correct quantity
      const inputQuantity = parseFloat(amount)
      const validation = validateQuantity(inputQuantity, currentSymbol, baseCurrency)
      
      if (!validation.isValid) {
        if (validation.correctedQuantity) {
          setOrderStatusError(`${validation.error}`)
          setAmount(validation.correctedQuantity.toString())
          setIsLoading(false)
          return
        } else {
          setOrderStatusError(validation.error || 'Invalid quantity')
          setIsLoading(false)
          return
        }
      }

      // Get the connector manager instance
      const connectorManager = ConnectorManager.getInstance()
      
      if (!connectorManager) {
        throw new Error('Connector manager not found')
      }
      
      // Get the enabled connector (binance or ninx)
      const enabledConnector = getSecureItem('broker_enabled')
      const connector = enabledConnector === 'binance' ? 
        connectorManager.getConnector('binance') :
        enabledConnector === 'ninx' ?
          connectorManager.getConnector('ninx') :
          null
      
      if (!connector) {
        throw new Error('Binance connector not found. Please connect to Binance first.')
      }

      // Use the corrected quantity
      const finalQuantity = validation.correctedQuantity || inputQuantity

      let result
      
      // Get the correct API keys based on enabled connector
      const apiKeyName = enabledConnector === 'binance' ? 'binance_api_key' : 'ninx_api_key'
      const apiSecretName = enabledConnector === 'binance' ? 'binance_api_secret' : 'ninx_api_secret'
      
      // Determine order type and place accordingly
      if (activeTab === 'Limit' && tpSlEnabled) {
        const hasTp = tpPrice && parseFloat(tpPrice) > 0
        const hasSl = slTriggerPrice && parseFloat(slTriggerPrice) > 0
        
        if (hasTp && hasSl) {
          // Both TP and SL → Use OTOCO
          const response = await fetch('/api/connectors/binance/otoco', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              apiKey: getSecureItem(apiKeyName),
              apiSecret: getSecureItem(apiSecretName),
              order: {
                symbol: currentSymbol,
                side: side,
                quantity: finalQuantity,
                workingPrice: parseFloat(price),
                tpPrice: parseFloat(tpPrice),
                slStopPrice: parseFloat(slTriggerPrice),
                slLimitPrice: slType === 'LIMIT' && slLimitPrice ? parseFloat(slLimitPrice) : undefined,
                slType: slType
              }
            })
          })
          
          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.message || 'Failed to place OTOCO order')
          }
          
          result = await response.json()
          setOrderStatusWithTimer(`OTOCO order placed successfully: ${result.orderListId}`)
          
        } else if (hasTp || hasSl) {
          // Only TP or only SL → Use OTO
          const pendingType = hasTp ? 'TP' : 'SL'
          const pendingPrice = hasTp ? parseFloat(tpPrice) : (slType === 'LIMIT' && slLimitPrice ? parseFloat(slLimitPrice) : undefined)
          const pendingStopPrice = hasSl ? parseFloat(slTriggerPrice) : (hasTp ? parseFloat(tpPrice) : undefined)
          
          const response = await fetch('/api/connectors/binance/oto', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              apiKey: getSecureItem(apiKeyName),
              apiSecret: getSecureItem(apiSecretName),
              order: {
                symbol: currentSymbol,
                side: side,
                quantity: finalQuantity,
                workingPrice: parseFloat(price),
                pendingType: pendingType,
                pendingPrice: pendingPrice,
                pendingStopPrice: pendingStopPrice
              }
            })
          })
          
          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.message || 'Failed to place OTO order')
          }
          
          result = await response.json()
          setOrderStatusWithTimer(`OTO order placed successfully: ${result.orderListId}`)
        }
      } else {
        // Regular order (no TP/SL) → Use existing logic
        const order = {
          symbol: currentSymbol,
          quantity: finalQuantity,
          price: (activeTab === 'Limit' || (activeTab === 'Stop' && price)) ? parseFloat(price) : undefined,
          type: activeTab.toUpperCase() as 'MARKET' | 'LIMIT' | 'STOP',
          side: side,
          triggerPrice: activeTab === 'Stop' ? parseFloat(triggerPrice) : undefined
        }
        
        console.log('Placing regular order with validated quantity:', order)

        // Place the order
        result = await connector.placeOrder(order)
        setOrderStatusWithTimer(`Order placed successfully: ${result.orderId || 'Order submitted'}`)
      }
      
      // Refresh balances after successful order with proper cache invalidation
      console.log('🔄 Refreshing balances after order placement...')
      
      // Add a small delay to allow exchange to update balances
      setTimeout(async () => {
        try {
          // Invalidate and refetch user assets cache
          await queryClient.invalidateQueries({ 
            queryKey: ['userAssets'] 
          })
          
          // Force an immediate refetch
          refetchAssets()
          
          console.log('✅ Balance refresh triggered successfully')
        } catch (error) {
          console.error('❌ Failed to refresh balances:', error)
        }
      }, 1000) // 1 second delay
      
      // Reset form fields
      resetForm()
      if (activeTab === 'Limit') {
        setPrice('')
        // Reset TP/SL fields if they were used
        if (tpSlEnabled) {
          setTpPrice('')
          setSlTriggerPrice('')
          setSlLimitPrice('')
          setTpSlEnabled(false)
        }
      }
      if (activeTab === 'Stop') {
        setTriggerPrice('')
      }
    } catch (error) {
      console.error('Order placement failed:', error)
      
      // Extract and format error message
      let errorMessage = 'Unknown error';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setOrderStatusError(`Order failed: ${errorMessage}`)
    } finally {
      setIsLoading(false)
    }
  }, [amount, currentSymbol, activeTab, price, triggerPrice, baseCurrency, refetchAssets, queryClient, setOrderStatusWithTimer, setOrderStatusError, setAmount, resetForm, tpSlEnabled, tpPrice, slTriggerPrice, slType, slLimitPrice]);
  
  const handleBuy = useCallback(() => {
    placeOrder('BUY')
  }, [placeOrder]);

  const handleSell = useCallback(() => {
    placeOrder('SELL')
  }, [placeOrder]);
  
  // Auto-trigger buy/sell if initialAction is provided
  // useEffect(() => {
  //   if (initialAction === 'BUY' && price) {
  //     handleBuy();
  //   } else if (initialAction === 'SELL' && price) {
  //     handleSell();
  //   }
  // }, [initialAction, price, handleBuy, handleSell]);

  // Use refs to store current state for event handler
  const activeTabRef = useRef(activeTab)
  const currentSymbolRef = useRef(currentSymbol)
  const executeVisualOrderRef = useRef(executeVisualOrder)
  
  // Update refs when state changes
  activeTabRef.current = activeTab
  currentSymbolRef.current = currentSymbol
  executeVisualOrderRef.current = executeVisualOrder

  // Register this component with AI Terminal and listen for real trading events (only once)
  useEffect(() => {
    // Create stable event handler that uses refs
    const handleRealPlaceOrder = async (event: Event) => {
      const customEvent = event as CustomEvent
      console.log('💰 BuySell: Received real place order event:', customEvent.detail)
      console.log('💰 BuySell: Event type:', customEvent.type)
      console.log('💰 BuySell: Current state - activeTab:', activeTabRef.current, 'currentSymbol:', currentSymbolRef.current)
      try {
        const result = await executeVisualOrderRef.current(customEvent.detail)
        console.log('💰 BuySell: Visual order execution result:', result)
        
        // Send back the result
        const resultEvent = new CustomEvent('realOrderResult', {
          detail: {
            success: true,
            message: `Real order completed for ${customEvent.detail.side} ${customEvent.detail.quantity} ${customEvent.detail.symbol}`,
            orderId: `real_${Date.now()}`,
            result,
            ...customEvent.detail,
            timestamp: new Date()
          }
        })
        window.dispatchEvent(resultEvent)
      } catch (error) {
        console.error('💰 BuySell: Visual order execution error:', error)
        const errorEvent = new CustomEvent('realOrderResult', {
          detail: {
            success: false,
            error: `Failed to execute real order: ${error}`,
            ...customEvent.detail
          }
        })
        window.dispatchEvent(errorEvent)
      }
    }

    registerComponent('buySell', {
      id: 'buySell',
      type: 'BuySell',
      name: 'Buy/Sell Trading Panel',
      capabilities: [
        {
          action: 'place_order',
          description: 'Place real trading orders with visual animations',
          parameters: [
            { name: 'symbol', type: 'string', description: 'Trading symbol' },
            { name: 'side', type: 'string', description: 'BUY or SELL' },
            { name: 'quantity', type: 'number', description: 'Order quantity' },
            { name: 'price', type: 'number', description: 'Order price' },
            { name: 'type', type: 'string', description: 'Order type' },
            { name: 'triggerPrice', type: 'number', description: 'Trigger price for stop orders' }
          ]
        }
      ],
      methods: {
        place_order: (params: any) => executeVisualOrderRef.current(params),
        executeVisualOrder: (params: any) => executeVisualOrderRef.current(params)
      }
    })

    window.addEventListener('realPlaceOrder', handleRealPlaceOrder)

    return () => {
      window.removeEventListener('realPlaceOrder', handleRealPlaceOrder)
    }
  }, []) // Empty dependency array - register only once

  return (  
    <div ref={containerRef} className={styles.container}>
      {/* Header with Title and Symbol Selector */}
      <div className={styles.header}>
        <span className={styles.title}>Trade</span>
        <div className={styles.controls}>
          {/* Display last price information */}
          {lastPrice && (
            <div className={styles.lastPriceDisplay}>
              LTP: {lastPrice} {priceChangePercent !== null && (
                <span className={priceChangePercent >= 0 ? styles.profit : styles.loss}>
                  ({priceChangePercent >= 0 ? '+' : ''}{priceChangePercent.toFixed(2)}%)
                </span>
              )}
            </div>
          )}
          {/* Symbol Selector */}
          <div className={styles.symbolSelector}>
            <button 
              ref={symbolButtonRef}
              onClick={() => setIsSymbolPopupOpen(true)}
              className={styles.symbolSelect}
              aria-label="Select Symbol"
            >
              {currentSymbol.toUpperCase()} ▾
            </button>
            
            {isSymbolPopupOpen && (
              <SymbolSearchModal
                onSelect={handleSymbolChange}
                onClose={() => setIsSymbolPopupOpen(false)}
                title="Select Trading Symbol"
                recentStorageKey="binanceTradingSymbols"
                customPopularSymbols={popularSymbols}
              />
            )}
          </div>
        </div>
      </div>
      
      {/* Animation Status */}
      <AnimationStatus isAnimating={isAnimating} lastAction={lastAction} />
      
      {/* Tab Header */}
      <div className={styles.tabHeader}>
        {TABS.map(t => (
          <div
            key={t}
            ref={t === 'Market' ? marketTabRef : t === 'Limit' ? limitTabRef : t === 'Stop' ? stopTabRef : null}
            className={`${styles.tab} ${activeTab === t ? styles.activeTab : ''}`}
            onClick={() => {
              setActiveTab(t)
              if (t === 'Market') {
                setPrice('')
                setTriggerPrice('')
              } else if (t === 'Limit') {
                setTriggerPrice('')
              } else if (t === 'Stop') {
                setPrice('')
              }
            }}
          >
            {t}
          </div>
        ))}
      </div>
      <div className={styles.tabContent}>
        {activeTab === 'Limit' && (
          <div className={styles.formRow}>
            <label>Price:</label>
            <input
              ref={priceRef}
              type="number"
              value={price}
              onChange={e => setPrice(e.target.value)}
              placeholder="Enter limit price"
            />
          </div>
        )}
        {activeTab === 'Stop' && (
          <>
            <div className={styles.formRow}>
              <label>Trigger Price:</label>
              <input
                ref={triggerPriceRef}
                type="number"
                value={triggerPrice}
                onChange={e => setTriggerPrice(e.target.value)}
                placeholder="Enter trigger price"
              />
            </div>
            <div className={styles.formRow}>
              <label>Limit Price (Optional):</label>
              <input
                ref={priceRef}
                type="number"
                value={price}
                onChange={e => setPrice(e.target.value)}
                placeholder="Leave empty for market execution"
              />
            </div>
          </>
        )}
        <div className={styles.formRow}>
          <label>Amount:</label>
          <div className={styles.inputWithBars}>
            <input
              ref={amountRef}
              type="number"
              value={amount}
              onChange={e => handleAmountChange(e.target.value)}
              placeholder={`Enter ${baseCurrency} amount`}
            />
            <PercentageBars
              selectedPercentage={selectedAmountPercentage}
              onPercentageClick={handleAmountPercentageClick}
              currency={baseCurrency}
              type="amount"
            />
          </div>
        </div>
        
        <div className={styles.formRow}>
          <label>Total:</label>
          <div className={styles.inputWithBars}>
            <input
              ref={totalRef}
              type="number"
              value={total}
              onChange={e => handleTotalChange(e.target.value)}
              onFocus={() => setIsEditingTotal(true)}
              onBlur={() => setIsEditingTotal(false)}
              placeholder={`Enter ${quoteCurrency} total`}
            />
            <PercentageBars
              selectedPercentage={selectedTotalPercentage}
              onPercentageClick={handleTotalPercentageClick}
              currency={quoteCurrency}
              type="total"
            />
          </div>
          {!lastPrice && (
            <small style={{ color: '#888', fontSize: '11px', marginTop: '2px' }}>
              {priceLoading ? 'Loading price...' : 'Price unavailable - manual calculation'}
            </small>
          )}
        </div>
        
        {/* TP/SL Section - Only show for Limit orders */}
        {activeTab === 'Limit' && (
          <div className={styles.tpSlSection}>
            <div className={styles.tpSlHeader}>
              <label className={styles.tpSlCheckbox}>
                <input
                  type="checkbox"
                  checked={tpSlEnabled}
                  onChange={(e) => setTpSlEnabled(e.target.checked)}
                />
                <span className={styles.checkboxLabel}>TP/SL</span>
              </label>
            </div>
            
            {tpSlEnabled && (
              <div className={styles.tpSlInputs}>
                {/* Take Profit Input */}
                <div className={styles.formRow}>
                  <label>TP Price:</label>
                  <input
                    type="number"
                    value={tpPrice}
                    onChange={(e) => setTpPrice(e.target.value)}
                    placeholder="Take profit price"
                  />
                </div>
                
                {/* Stop Loss Section */}
                <div className={styles.formRow}>
                  <label>SL Trigger:</label>
                  <input
                    type="number"
                    value={slTriggerPrice}
                    onChange={(e) => setSlTriggerPrice(e.target.value)}
                    placeholder="Stop loss trigger price"
                  />
                </div>
                
                {/* Stop Loss Type Selection */}
                <div className={styles.formRow}>
                  <label>SL Type:</label>
                  <div className={styles.slTypeSelector}>
                    <button
                      type="button"
                      className={`${styles.slTypeButton} ${slType === 'MARKET' ? styles.active : ''}`}
                      onClick={() => setSlType('MARKET')}
                    >
                      Market
                    </button>
                    <button
                      type="button"
                      className={`${styles.slTypeButton} ${slType === 'LIMIT' ? styles.active : ''}`}
                      onClick={() => setSlType('LIMIT')}
                    >
                      Limit
                    </button>
                  </div>
                </div>
                
                {/* Stop Loss Limit Price - Only show when SL Type is Limit */}
                {slType === 'LIMIT' && (
                  <div className={styles.formRow}>
                    <label>SL Price:</label>
                    <input
                      type="number"
                      value={slLimitPrice}
                      onChange={(e) => setSlLimitPrice(e.target.value)}
                      placeholder="Stop loss limit price"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Auto-fix quantity validation issues */}
        {useMemo(() => {
          // Don't auto-fix when amount is being updated from total input or user is editing total
          if (!amount || isEditingTotal) return null
          
          // Auto-apply correction for step size issues (but not minimum quantity issues)
          if (!quantityValidation.isValid && quantityValidation.correctedQuantity && 
              quantityValidation.error?.includes('multiple of')) {
            // Automatically apply the correction
            setTimeout(() => {
              setAmount(quantityValidation.correctedQuantity!.toString())
              // Also update total when fixing amount (only if price is available)
              if (lastPrice && lastPrice > 0) {
                const newTotal = calculateTotal(quantityValidation.correctedQuantity!.toString())
                setTotal(newTotal)
              }
            }, 100) // Small delay to avoid conflicts
            
            return (
              <div style={{ marginTop: '4px', fontSize: '12px' }}>
                <small style={{ color: '#f0b90b' }}>
                  Auto-corrected to: {quantityValidation.correctedQuantity} {baseCurrency}
                </small>
              </div>
            )
          }
          
          // Show error for minimum quantity issues (don't auto-fix these)
          if (!quantityValidation.isValid && !quantityValidation.correctedQuantity) {
            return (
              <div style={{ marginTop: '4px', fontSize: '12px' }}>
                <small style={{ color: '#ff6b6b' }}>
                  {quantityValidation.error}
                </small>
              </div>
            )
          }
          
          return null
        }, [amount, quantityValidation, baseCurrency, isEditingTotal, lastPrice, calculateTotal, setAmount, setTotal])}
        
        {/* Balance Display */}
        <div className={styles.balanceContainer}>
          <div className={styles.balanceRow}>
            <span className={styles.balanceLabel}>
              Available:
              {/* <button 
                onClick={() => refetchAssets()}
                style={{ 
                  marginLeft: '8px',
                  padding: '1px 4px',
                  fontSize: '10px',
                  background: 'transparent',
                  color: '#88A1AC',
                  border: '1px solid #88A1AC',
                  borderRadius: '3px',
                  cursor: 'pointer'
                }}
                title="Refresh balances"
                disabled={assetsLoading}
              >
                {assetsLoading ? '⟳' : '↻'}
              </button> */}
            </span>
            <div className={styles.balanceValues}>
              <span className={styles.balanceItem}>
                {assetsLoading ? '...' : `${quoteBalance.toFixed(8)} ${quoteCurrency}`}
              </span>
              <span className={styles.balanceSeparator}>|</span>
              <span className={styles.balanceItem}>
                {assetsLoading ? '...' : `${baseBalance.toFixed(8)} ${baseCurrency}`}
              </span>
            </div>
          </div>
          {assetsError && (
            <div style={{ fontSize: '10px', color: '#ff6b6b', marginTop: '4px' }}>
              {assetsError.message || 'Failed to load balances'}
            </div>
          )}
        </div>
        
        <div className={styles.buttons}>
          <div className={styles.buttonGroup}>
            <button 
              ref={buyButtonRef}
              className={styles.buyButton} 
              onClick={handleBuy}
              disabled={isLoading || !amount || (activeTab === 'Limit' && !price) || (activeTab === 'Stop' && !triggerPrice)}
            >
              {isLoading ? 'Processing...' : 'Buy'}
            </button>
            {/* {!assetsLoading && (
              <small className={styles.buttonBalanceBelow}>Avbl: {quoteBalance.toFixed(4)} {quoteCurrency}</small>
            )} */}
          </div>
          <div className={styles.buttonGroup}>
            <button 
              ref={sellButtonRef}
              className={styles.sellButton} 
              onClick={handleSell}
              disabled={isLoading || !amount || (activeTab === 'Limit' && !price) || (activeTab === 'Stop' && !triggerPrice)}
            >
              {isLoading ? 'Processing...' : 'Sell'}
            </button>
            {/* {!assetsLoading && (
              <small className={styles.buttonBalanceBelow}>Avbl: {baseBalance.toFixed(4)} {baseCurrency}</small>
            )} */}
          </div>
        </div>
        
        {orderStatus && (
          <div className={`${styles.orderStatus} ${orderStatus.includes('failed') || orderStatus.includes('Please') ? styles.error : styles.success}`}>
            <span>{orderStatus}</span>
            <button 
              className={styles.closeButton}
              onClick={closeOrderStatus}
              title="Close"
            >
              ×
            </button>
          </div>
        )}
      </div>
    </div>
  )
} 

export default memo(BuySellComponent);
